import { PRODUCTS, type Product } from './types'

/* The single route source (MS-818; spec §1-ctrl-route): every page slug, its
   nav label, and whether it carries the Mambu MCP / Mambu Agents tab segment.
   Mirrors the prototype's `SECTION_ROUTE`/`tabToRoute`/`routeToTab` maps
   (subscription-portal.html), but as a clean path scheme — `/{page}/{tab}`,
   no `#` (the prototype's hash is a file:// workaround; see
   ARCHITECTURE_DECISIONS.md §40). AppShell's nav, Home's launchers,
   Breadcrumbs, the route table and every in-app link read from here rather
   than re-declaring a path literal. */

export type PageSlug =
  | 'subscription'
  | 'additional-volume'
  | 'excess-volume'
  | 'user-limits'
  | 'monthly-limits'
  | 'tenant-limits'
  | 'usage'
  | 'settings'

export type PageLabelKey =
  | 'appShell.nav.subscription'
  | 'appShell.nav.additionalVolume'
  | 'appShell.nav.excessVolume'
  | 'appShell.nav.userLimits'
  | 'appShell.nav.monthlyLimits'
  | 'appShell.nav.tenantLimits'
  | 'appShell.nav.usage'
  | 'appShell.nav.settings'

/** A page's one fixed tab (MS-997: Settings › Access) — the prototype's
 * `FIXED_TABS`: a tab set that never depends on the contract, unlike the
 * product tabs. Its segment is part of the page's path. */
export interface FixedTab {
  slug: 'access'
  labelKey: 'settings.tabs.access'
}

export interface PageDef {
  slug: PageSlug
  labelKey: PageLabelKey
  /** True for the five pages with the Mambu MCP / Mambu Agents product tabs.
   * Monthly and Tenant Limits have none (spec route table; prototype
   * `TABBED`). */
  tabbed: boolean
  /** The page's one fixed tab, if it has one (Settings › Access). */
  fixedTab?: FixedTab
  /** Admins only (MS-997 §1-ctrl-vis): no nav item for a Controller, and
   * the route is guarded by app/RequirePortalAdmin.tsx rather than created by
   * the generic page mapper. */
  adminOnly?: true
}

export const PAGES: Readonly<Record<PageSlug, PageDef>> = {
  subscription: { slug: 'subscription', labelKey: 'appShell.nav.subscription', tabbed: true },
  'additional-volume': {
    slug: 'additional-volume',
    labelKey: 'appShell.nav.additionalVolume',
    tabbed: true,
  },
  'excess-volume': { slug: 'excess-volume', labelKey: 'appShell.nav.excessVolume', tabbed: true },
  'user-limits': { slug: 'user-limits', labelKey: 'appShell.nav.userLimits', tabbed: true },
  'monthly-limits': {
    slug: 'monthly-limits',
    labelKey: 'appShell.nav.monthlyLimits',
    tabbed: false,
  },
  'tenant-limits': { slug: 'tenant-limits', labelKey: 'appShell.nav.tenantLimits', tabbed: false },
  usage: { slug: 'usage', labelKey: 'appShell.nav.usage', tabbed: true },
  settings: {
    slug: 'settings',
    labelKey: 'appShell.nav.settings',
    tabbed: false,
    fixedTab: { slug: 'access', labelKey: 'settings.tabs.access' },
    adminOnly: true,
  },
}

export const TABBED_PAGES: readonly PageSlug[] = (Object.keys(PAGES) as PageSlug[]).filter(
  (slug) => PAGES[slug].tabbed
)

export function pageFromSlug(slug: string | undefined): PageDef | undefined {
  if (!slug || !Object.hasOwn(PAGES, slug)) return undefined
  return PAGES[slug as PageSlug]
}

export const PRODUCT_SLUG: Readonly<Record<Product, string>> = {
  'Mambu MCP': 'mambu-mcp',
  'Mambu Agents': 'mambu-agents',
}

/** The tab a bare tabbed page lands on (prototype `applyRoute`: MCP). */
export const DEFAULT_PRODUCT: Product = PRODUCTS[0]

export function productFromSlug(slug: string | undefined): Product | undefined {
  return PRODUCTS.find((p) => PRODUCT_SLUG[p] === slug)
}

/** `/{page}` or, with a product on a tabbed page, `/{page}/{tab}`. A page
 * with a fixed tab always names it (`/settings/access`). */
export function pagePath(page: PageSlug, product?: Product): string {
  const def = PAGES[page]
  if (def.fixedTab) return `/${page}/${def.fixedTab.slug}`
  if (product && def.tabbed) return `/${page}/${PRODUCT_SLUG[product]}`
  return `/${page}`
}

/** The `handle` every page route carries, so the breadcrumb (and AppShell's
 * crumb-bar spacing) read the route React Router actually matched — with its
 * case-insensitive matching and decoded params — instead of re-parsing the
 * raw pathname (pageRoutes.tsx sets these; hooks/useCrumbTrail.ts reads them).
 * - `home`: the Home route (no breadcrumb there).
 * - `page`: the known page this route sits under (set on the page's parent
 *   route, so every child — the tab route, a not-found child — inherits it).
 * - `tab`: the `/{page}/:tab` route itself, whose `:tab` param names the
 *   product. A deeper path under it (`/{page}/:tab/*`) does not carry it.
 * - `fixedTab`: the route of a page's fixed tab (`/settings/access`).
 * - `adminOnly`: the route sits behind RequirePortalAdmin. */
export interface RouteHandle {
  home?: true
  page?: PageSlug
  tab?: true
  fixedTab?: true
  adminOnly?: true
}

/** Pre-MS-818 slugs, kept as replace-redirects so old bookmarks and links
 * still land (the prototype also accepts its legacy ids). */
export const LEGACY_REDIRECTS: Readonly<Record<string, PageSlug>> = {
  volume: 'additional-volume',
  limits: 'user-limits',
}

/** The routes outside the AppShell (MS-977; ARCHITECTURE_DECISIONS.md §52):
 * the sign-in hand-off pages render without a session, so App's Gate and
 * the AuthProvider's mount probe let them through by path. `login` is where
 * the backend's Studio hand-off lands (GET /api/v1/auth/login redirects
 * there) and where an expired pending login sends the user back;
 * `selectAccount` is the post-login account choice, guarded by the
 * pending-login cookie rather than by a session. */
export const AUTH_PATHS = {
  login: '/login',
  selectAccount: '/select-account',
  oauthCallback: '/oauth2/callback',
  accessDenied: '/access-denied',
} as const

/** `/login?error=auth`: Login after a failed Cognito sign-in (MS-993) shows
 * the prototype's fixed "Authentication failed" line. */
export const AUTH_FAILED_PARAM = 'error'
export const AUTH_FAILED_VALUE = 'auth'
export const AUTH_FAILED_LOGIN_PATH = `${AUTH_PATHS.login}?${AUTH_FAILED_PARAM}=${AUTH_FAILED_VALUE}`

/** `/login?reauth=1` (MS-997 G5/G6): the sign-in page after the daily
 * sign-in fell due or Cognito refused the refresh token. It shows the "sign in
 * again" notice, and its button asks the hosted page for the password even
 * while Cognito's own sign-in cookie is still valid (`prompt=login`). */
export const REAUTH_PARAM = 'reauth'
export const REAUTH_LOGIN_PATH = `${AUTH_PATHS.login}?${REAUTH_PARAM}=1`
export const COGNITO_LOGIN_PATH = '/api/v1/auth/cognito/login'

/** Paths that render through the router even with no session (Gate). */
export const SESSIONLESS_PATHS: readonly string[] = [
  AUTH_PATHS.oauthCallback,
  AUTH_PATHS.accessDenied,
  AUTH_PATHS.selectAccount,
]
